// src/components/Analytics.js
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

// ─── Viewport hook (matches the pattern used elsewhere) ───
const useViewport = () => {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1280
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return {
    width,
    isMobile: width < 640,
    isTablet: width >= 640 && width < 1024,
    isDesktop: width >= 1024,
  };
};

const safeNumber = (value, fallback = 0) =>
  typeof value === 'number' && !Number.isNaN(value) ? value : fallback;

const safeRate = (value, fallback = 0) => {
  const n = safeNumber(value, fallback);
  return n > 1 ? n : n * 100;
};

const Analytics = () => {
  const { isMobile, isTablet } = useViewport();

  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          console.warn('[Analytics] No authToken — skipping backend fetch');
          setError('You must be logged in to view analytics.');
          setLoading(false);
          return;
        }

        const response = await api.getDashboardStats();
        // Handle both Spring Boot wrapped data and direct responses
        const payload = response?.data || response || {};
        console.log('[Analytics] Loaded metrics:', payload);
        setMetrics(payload);
      } catch (err) {
        console.error('[Analytics] Failed to load:', err);
        setError(
          'Failed to load analytics data from the server. Please try again later.'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          padding: '50px',
          textAlign: 'center',
          color: '#64748b',
        }}
      >
        Loading Analytics Dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '30px',
          maxWidth: 600,
          margin: '50px auto',
          textAlign: 'center',
          color: '#ef4444',
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
        }}
      >
        {error}
      </div>
    );
  }

  // ─── Safe metric extraction ───
  const totalReports = safeNumber(metrics?.totalReports);
  const totalAiReports = safeNumber(metrics?.totalAiReports);
  const totalManualReports = safeNumber(metrics?.totalManualReports);
  const aiUsageRate = safeRate(metrics?.aiUsageRate);
  const aiAcceptanceRate = safeRate(metrics?.aiReportAcceptanceRate);
  const aiReportsAccepted = safeNumber(metrics?.aiReportsAccepted);
  const hoursSaved = safeNumber(metrics?.estimatedHoursSaved);
  const dailyActiveUsers = safeNumber(metrics?.dailyActiveUsers);
  const avgReportsPerUser = safeNumber(metrics?.avgReportsPerUser);
  const revisionRate = safeRate(metrics?.reportRevisionRate);
  const avgAiSeconds = safeNumber(metrics?.averageAiGenerationSeconds);

  const containerPadding = isMobile
    ? '16px'
    : isTablet
    ? '24px'
    : '30px';

  const cardCols = isMobile
    ? '1fr'
    : isTablet
    ? 'repeat(2, 1fr)'
    : 'repeat(auto-fit, minmax(220px, 1fr))';

  return (
    <div
      className="fade-in"
      style={{
        padding: containerPadding,
        maxWidth: '1100px',
        margin: '0 auto',
        fontFamily: "'Inter', sans-serif",
        boxSizing: 'border-box',
      }}
    >
      <div style={{ marginBottom: 24 }}>
        <h2
          style={{
            margin: '0 0 8px 0',
            color: '#0f172a',
            fontSize: isMobile ? '1.35rem' : '1.7rem',
            fontWeight: 800,
          }}
        >
          Analytics Dashboard
        </h2>
        <p
          style={{
            margin: 0,
            color: '#64748b',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}
        >
          Overview of report generations, AI utilization rates, and operational
          efficiency metrics.
        </p>
      </div>

      {/* Top grid metrics */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: cardCols,
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div style={cardStyle}>
          <span style={labelStyle}>Total Reports</span>
          <h3 style={valStyle}>{totalReports}</h3>
          <small style={subStyle}>
            {totalAiReports} AI / {totalManualReports} Manual
          </small>
        </div>

        <div style={cardStyle}>
          <span style={labelStyle}>AI Usage Rate</span>
          <h3 style={{ ...valStyle, color: '#2563eb' }}>
            {aiUsageRate.toFixed(1)}%
          </h3>
          <small style={subStyle}>Proportion of AI-assisted outputs</small>
        </div>

        <div style={cardStyle}>
          <span style={labelStyle}>AI Acceptance Rate</span>
          <h3 style={{ ...valStyle, color: '#16a34a' }}>
            {aiAcceptanceRate.toFixed(1)}%
          </h3>
          <small style={subStyle}>
            {aiReportsAccepted} accepted without major edits
          </small>
        </div>

        <div style={cardStyle}>
          <span style={labelStyle}>Estimated Hours Saved</span>
          <h3 style={{ ...valStyle, color: '#9333ea' }}>
            {hoursSaved.toFixed(1)} hrs
          </h3>
          <small style={subStyle}>Total clinical time saved</small>
        </div>
      </div>

      {/* Detailed breakdown */}
      <div
        style={{
          background: 'white',
          padding: isMobile ? '20px' : '25px',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
        }}
      >
        <h3
          style={{
            margin: '0 0 20px 0',
            color: '#1e293b',
            fontSize: '1.15rem',
            fontWeight: 600,
          }}
        >
          Operational Efficiency
        </h3>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? 0 : 20,
          }}
        >
          <div style={rowStyle}>
            <span style={{ color: '#475569' }}>
              Daily Active Users (DAU):
            </span>
            <strong style={{ color: '#0f172a' }}>{dailyActiveUsers}</strong>
          </div>
          <div style={rowStyle}>
            <span style={{ color: '#475569' }}>Avg Reports Per User:</span>
            <strong style={{ color: '#0f172a' }}>
              {avgReportsPerUser.toFixed(2)}
            </strong>
          </div>
          <div style={rowStyle}>
            <span style={{ color: '#475569' }}>Report Revision Rate:</span>
            <strong style={{ color: '#0f172a' }}>
              {revisionRate.toFixed(1)}%
            </strong>
          </div>
          <div style={rowStyle}>
            <span style={{ color: '#475569' }}>
              Avg AI Generation Time:
            </span>
            <strong style={{ color: '#0f172a' }}>
              {avgAiSeconds.toFixed(2)} sec
            </strong>
          </div>
        </div>

        {metrics?.unavailableMetricsNote && (
          <div
            style={{
              marginTop: 20,
              padding: 12,
              background: '#f8fafc',
              borderLeft: '3px solid #cbd5e1',
              fontSize: '0.83rem',
              color: '#64748b',
              borderRadius: 4,
            }}
          >
            <strong>Note:</strong> {metrics.unavailableMetricsNote}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Shared styles ───
const cardStyle = {
  padding: 20,
  borderRadius: 12,
  background: 'white',
  border: '1px solid #e2e8f0',
  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
  minWidth: 0,
};

const labelStyle = {
  fontSize: '0.78rem',
  color: '#64748b',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
};

const valStyle = {
  fontSize: '1.7rem',
  fontWeight: 800,
  color: '#0f172a',
  margin: '8px 0 4px 0',
};

const subStyle = {
  color: '#64748b',
  fontSize: '0.78rem',
};

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: '1px solid #f1f5f9',
  fontSize: '0.9rem',
  gap: 12,
};

export default Analytics;